import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export function SidebarMenuItem({ item, isActive, onSelect, selectedTab, setSelectedTab, collapsed = false, onRequestExpand }) {

    const navigate = useNavigate();
    const location = useLocation();
    const currentPath = location.pathname.split('/')[1];
    
    const [isExpanded, setIsExpanded] = useState(false);
    const [height, setHeight] = useState(0);
    const contentRef = useRef(null);

    // Update height when expanded state changes
    useEffect(() => {
        if (contentRef.current) {
            if (isExpanded) {
                setHeight(contentRef.current.scrollHeight);
            } else {
                setHeight(0);
            }
        }
    }, [isExpanded]);

    const handleClick = () => {
        // Leaf item (no subItems) — just navigate
        if(!item.subItems) {
            setSelectedTab(item.id);
            navigate(item.url);
            return;
        }
        // Parent item (has subItems)
        onSelect(item.id);
        // In collapsed mode, expand the sidebar AND open this item's sub-menu
        // — never navigate to the parent URL (which is often blank/listing-only
        // and shows a blank page).
        if (collapsed) {
            if (onRequestExpand) onRequestExpand();
            setIsExpanded(true);
            return;
        }
        setIsExpanded(!isExpanded);
    };

    return (
        <div className={`flex flex-col ${collapsed ? 'w-auto' : 'w-full'}`}>
            <button
                onClick={handleClick}
                title={collapsed ? item.title : undefined}
                className={`flex items-center gap-1.5 mt-4 bg-navBtn rounded-md font-semibold ${currentPath === item.id ? 'text-primary bg-opacity-10' : 'text-gray-900'} ${collapsed ? 'justify-center w-10 h-10 p-0' : 'w-full justify-between px-3 py-2'}`}
                aria-expanded={isExpanded}
                aria-label={collapsed ? item.title : undefined}
            >
                {item.icon && (
                    typeof item.icon === 'string' ? (
                        <img
                            src={item.icon}
                            alt=""
                            className="object-contain shrink-0 aspect-square h-6 w-6 text-current"
                        />
                    ) : (
                        <item.icon
                            size={collapsed ? 20 : 16}
                            className={`shrink-0 text-current ${currentPath === item.id ? 'text-primary' : 'text-secondary'}`}
                        />
                    )
                )}
                {!collapsed && (
                    <>
                        <span className="grow shrink my-auto text-left font-extrabold text-base w-full">{item.title}</span>
                        {
                            item.subItems && (
                                <span className={`grow shrink my-auto text-left font-extrabold text-base transition-transform duration-300 ${isExpanded ? 'rotate-180' : 'rotate-0'}`}>
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </span>
                            )
                        }
                    </>
                )}
            </button>

            {item.subItems && !collapsed && (
                <div
                    ref={contentRef}
                    className="overflow-hidden transition-all duration-300 ease-in-out"
                    style={{ height: `${height}px` }}
                >
                    <div className="flex flex-col items-start px-6 mt-0">
                        {item.subItems.map((subItem) => (
                            <div key={subItem.id} className="mt-2 w-full">
                                <button 
                                    className={`${selectedTab === subItem.id ? 'text-primary' : 'text-black'} font-bold text-left text-base transition-colors duration-200 hover:text-primary`} 
                                    onClick={() => { 
                                        const itemUrl = item.url || '';
                                        setSelectedTab(subItem.subItem ? subItem.subItem.id : subItem.id);
                                        // console.log(subItem.subItems ? itemUrl+subItem.url+subItem.subItems.url : itemUrl+subItem.url);
                                        navigate(subItem.subItems ? itemUrl+subItem.url+subItem.subItems.url : itemUrl+subItem.url);
                                    }}
                                    disabled={subItem.subItems ? true : false}
                                >{subItem.label}</button>
                                {subItem.subItems && (
                                    <div className="flex flex-col w-full">
                                        {subItem.subItems.map((nestedItem) => (
                                            <button
                                                key={nestedItem.id}
                                                className="flex gap-2 mt-2 text-gray-900 text-base transition-colors duration-200 hover:text-primary"
                                                onClick={() => {
                                                    const itemUrl = item.url || '';
                                                    setSelectedTab(nestedItem.id);
                                                    navigate(itemUrl + subItem.url + nestedItem.url);
                                                }}
                                            >
                                                <div className={`flex shrink-0 my-auto w-2 h-0.5 ${selectedTab === nestedItem.id ? 'bg-primary' : 'bg-gray-300'}`} />
                                                <span className={`${selectedTab === nestedItem.id ? 'text-primary' : 'text-black'} text-left font-semibold text-base`}>{nestedItem.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}